import { useContext } from 'react';

import { Box, Button, Text } from '@zillow/constellation-10';
import { LoadingButton } from '@zg-rentals/loading-components';
import { LoadingMapContext } from '../../../../providers/LoadingMapContext/context';

import { PERMISSION_REQUEST_TYPE } from '../../../../constants/ApplicationPermissionsConstants';
import { StyledHeadingLink } from '../../styles';

import type { ApplicationsSummary } from '../../interfaces';

type Props = {
  permissionType: string | null;
  onSetApplicationManagerClick: () => void;
  applicationsSummary: ApplicationsSummary;
};

const ApplicationsHeader = ({ applicationsSummary, permissionType, onSetApplicationManagerClick }: Props) => {
  const {
    loadingByComponent: { ConnectedApplications: isApplicationsLoading },
  } = useContext(LoadingMapContext);

  const title = 'Applications';

  if (!applicationsSummary || isApplicationsLoading) {
    return (
      <Box
        data-testid="ApplicationsHeader"
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Text
          asChild
          css={{
            textStyle: { base: 'body-bold', lg: 'heading-xs' },
            paddingBottom: '400',
          }}
        >
          <h3>{title}</h3>
        </Text>
        <LoadingButton size="sm" isLoading={isApplicationsLoading} loadingWidth="20%">
          &nbsp;
        </LoadingButton>
      </Box>
    );
  }

  const {
    urlPath,
    rentalApplicationData: {
      totalNumberOfWithdrawnApplications,
      totalNumberOfActiveApplications,
      totalNumberOfDeclinedApplications,
    },
  } = applicationsSummary;

  const totalNumberOfAllApplications =
    totalNumberOfDeclinedApplications + totalNumberOfWithdrawnApplications + totalNumberOfActiveApplications;

  // not sure of the difference, but wanted to use the same logic as ConnectedApplications
  const applicationCardListLength = applicationsSummary?.rentalApplicationData?.applicationList?.length || 0;
  const hasApplications = applicationCardListLength > 0;

  const viewAllApplicationsText = `View more (${totalNumberOfAllApplications})`;

  const renderViewAllApplicationsLink = () => {
    if (
      totalNumberOfAllApplications < 1 ||
      permissionType === PERMISSION_REQUEST_TYPE.REQUEST_ACCESS ||
      permissionType === PERMISSION_REQUEST_TYPE.NO_PERMISSION
    ) {
      return null;
    }

    if (permissionType === PERMISSION_REQUEST_TYPE.SET_APPLICATION_MANAGER) {
      return (
        <StyledHeadingLink>
          <Button
            tone="neutral"
            emphasis="outlined"
            size="sm"
            onClick={onSetApplicationManagerClick}
            data-testid="view-all-applications-cta"
          >
            {viewAllApplicationsText}
          </Button>
        </StyledHeadingLink>
      );
    }

    return (
      <StyledHeadingLink>
        <Button asChild tone="neutral" emphasis="outlined" size="sm" data-testid="view-all-applications-cta">
          <a href={urlPath}>{viewAllApplicationsText}</a>
        </Button>
      </StyledHeadingLink>
    );
  };

  return (
    <Box
      data-testid="ApplicationsHeader"
      css={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {hasApplications && (
        <Text
          asChild
          css={{
            textStyle: { base: 'body-bold', lg: 'heading-xs' },
          }}
        >
          <h3>{title}</h3>
        </Text>
      )}

      {renderViewAllApplicationsLink()}
    </Box>
  );
};

export default ApplicationsHeader;
